/**
 * Component Boutiques Lookbooks
 *
 * Season tabs + fullscreen flipbook viewer for the /pages/boutiques lookbooks section.
 *
 * @module component-boutiques-lookbooks
 * @version 1.0.0
 * @extends HTMLElement
 */

customElements.component = 'component-boutiques-lookbooks';

if (!customElements.get(customElements.component)) {
    class componentBoutiquesLookbooks extends HTMLElement {

        /**
         * Boutiques lookbooks web component.
         *
         * @constructor
         */
        constructor() {
            super();

            this.seasons = [];
            this.currentSeason = null;
            this.currentIndex = 0;
            this.renderToken = 0;
            this.loadedImages = {};

            this.initEvents();
        }

        /**
         * Init component events.
         *
         * @returns {undefined}
         */
        initEvents() {
            const dataEl = this.querySelector('[data-boutiques-lookbooks-data]');

            try {
                this.seasons = JSON.parse((dataEl && dataEl.textContent) || '[]');
            } catch (e) {
                this.seasons = [];
            }

            this.viewer = this.querySelector('[data-flipbook-viewer]');
            if (!this.viewer) return false;

            this.imageEl = this.viewer.querySelector('[data-flipbook-image]');
            this.counterEl = this.viewer.querySelector('[data-flipbook-counter]');
            this.counterTemplate = this.viewer.getAttribute('data-counter-template') || 'Spread {{ current }} of {{ total }}';
            this.altTemplate = this.viewer.getAttribute('data-alt-template') || '{{ season }} spread {{ index }}';

            this.querySelectorAll('[data-flipbook-open]').forEach((btn) => {
                btn.addEventListener('click', () => {
                    const seasonId = btn.getAttribute('data-season-id');
                    const index = btn.hasAttribute('data-spread-index') ? parseInt(btn.getAttribute('data-spread-index'), 10) : 0;
                    this.openFlipbook(seasonId, index);
                });
            });

            this.viewer.querySelector('[data-flipbook-prev]').addEventListener('click', () => this.step(-1));
            this.viewer.querySelector('[data-flipbook-next]').addEventListener('click', () => this.step(1));
            this.viewer.querySelector('[data-flipbook-close]').addEventListener('click', () => this.closeFlipbook());
            this.viewer.addEventListener('click', (event) => {
                if (event.target === this.viewer) this.closeFlipbook();
            });

            document.addEventListener('keydown', (event) => {
                if (this.viewer.hidden) return;
                if (event.key === 'ArrowRight') this.step(1);
                if (event.key === 'ArrowLeft') this.step(-1);
                if (event.key === 'Escape') this.closeFlipbook();
            });

            this.querySelectorAll('[data-season-tab]').forEach((tab) => {
                tab.addEventListener('click', () => {
                    const seasonId = tab.getAttribute('data-season-id');

                    this.querySelectorAll('[data-season-tab]').forEach((t) => {
                        const active = t === tab;
                        t.classList.toggle('boutiques-lookbooks__tab--active', active);
                        t.setAttribute('aria-selected', active ? 'true' : 'false');
                    });

                    this.querySelectorAll('[data-season-panel]').forEach((panel) => {
                        panel.hidden = panel.getAttribute('data-season-id') !== seasonId;
                    });

                    this.preloadSeason(this.findSeason(seasonId));
                });
            });

            // Warm the cache for whichever season is visible on load, so the
            // first "Flipbook view" click doesn't have to fetch anything.
            const activeTab = this.querySelector('[data-season-tab].boutiques-lookbooks__tab--active') || this.querySelector('[data-season-tab]');
            if (activeTab) {
                this.preloadSeason(this.findSeason(activeTab.getAttribute('data-season-id')));
            }
        }

        /**
         * Find a season by its block ID.
         *
         * @param {string} id A season block ID.
         * @returns {object|null} The matching season, or null.
         */
        findSeason(id) {
            for (let i = 0; i < this.seasons.length; i++) {
                if (this.seasons[i].id === id) return this.seasons[i];
            }
            return null;
        }

        /**
         * Fetch an image into the browser cache, resolving once it's ready
         * to be shown without a visible loading delay.
         *
         * @param {string} url An image URL.
         * @returns {Promise} Resolves once loaded (or on error, so callers never hang).
         */
        preloadImage(url) {
            if (!url || this.loadedImages[url]) return Promise.resolve();

            return new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                    this.loadedImages[url] = true;
                    resolve();
                };
                img.onerror = () => resolve();
                img.src = url;
            });
        }

        /**
         * Warm the cache for every spread in a season, without blocking the caller.
         *
         * @param {object} season A season object with an images array.
         * @returns {undefined}
         */
        preloadSeason(season) {
            if (!season) return;
            season.images.forEach((url) => this.preloadImage(url));
        }

        /**
         * Render the current flipbook frame. Waits for the target image to be
         * fully loaded before swapping it in, so the viewer never flashes a
         * stale or mismatched frame while the new one is still loading.
         *
         * @returns {undefined}
         */
        renderFrame() {
            if (!this.currentSeason || !this.currentSeason.images.length) return;

            const url = this.currentSeason.images[this.currentIndex] || '';
            const alt = this.altTemplate
                .replace('{{ season }}', this.currentSeason.name)
                .replace('{{ index }}', this.currentIndex + 1);
            const counter = this.counterTemplate
                .replace('{{ current }}', this.currentIndex + 1)
                .replace('{{ total }}', this.currentSeason.images.length);

            this.renderToken += 1;
            const token = this.renderToken;

            this.counterEl.textContent = counter;
            this.viewer.classList.add('boutiques-flipbook--loading');

            this.preloadImage(url).then(() => {
                // A newer navigation already happened while this one was loading; drop it.
                if (token !== this.renderToken) return;

                this.imageEl.src = url;
                this.imageEl.alt = alt;
                this.viewer.classList.remove('boutiques-flipbook--loading');
            });
        }

        /**
         * Open the fullscreen flipbook viewer.
         *
         * @param {string} seasonId A season block ID.
         * @param {number} index Starting spread index.
         * @returns {undefined}
         */
        openFlipbook(seasonId, index) {
            const season = this.findSeason(seasonId);
            if (!season || !season.images.length) return;

            this.currentSeason = season;
            this.currentIndex = Math.max(0, Math.min(index || 0, season.images.length - 1));
            this._triggerEl = document.activeElement;

            this.preloadSeason(season);
            this.renderFrame();

            this.viewer.hidden = false;
            document.body.style.overflow = 'hidden';
            this.viewer.querySelector('[data-flipbook-close]').focus();
        }

        /**
         * Close the fullscreen flipbook viewer.
         *
         * @returns {undefined}
         */
        closeFlipbook() {
            this.viewer.hidden = true;
            document.body.style.overflow = '';

            if (this._triggerEl) this._triggerEl.focus();
        }

        /**
         * Step the flipbook forward/backward, wrapping at the ends.
         *
         * @param {number} delta -1 or 1.
         * @returns {undefined}
         */
        step(delta) {
            if (!this.currentSeason || !this.currentSeason.images.length) return;
            const len = this.currentSeason.images.length;
            this.currentIndex = (this.currentIndex + delta + len) % len;
            this.renderFrame();
        }
    }

    customElements.define(customElements.component, componentBoutiquesLookbooks);
}
